<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
// NOTICE: KAPTION-INTEGRATION — team-availability-guard.UI.1
import {
  isTeamAssignable,
  teamTitleWithAvailability,
  teamAvailabilityStatus,
} from 'dashboard/helper/teamAvailability';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useAssistantsList } from 'dashboard/composables/useAssistantsList';
// NOTICE: KAPTION-INTEGRATION — smart-availability.UI.1 / .UI.5 / .PREFERRED.3
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import {
  teamTitleWithQueue,
  teamStatusFor,
} from 'dashboard/helper/kaption/smartAvailability';
import KaptionSmartAssignmentAPI from 'dashboard/api/kaptionSmartAssignment';
import { smartAssignmentAlertMessage } from 'dashboard/helper/kaption/smartAssignment';
// NOTICE: KAPTION-INTEGRATION - Client-managed assistant assignment permission.
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList, assignableAgents, smartAvailabilityActive } =
      useAgentsList();
    // NOTICE: KAPTION-INTEGRATION
    const { assistantsList } = useAssistantsList();
    // NOTICE: KAPTION-INTEGRATION
    const { canUseFeature } = useFeaturePermissions();
    // NOTICE: KAPTION-INTEGRATION — smart-availability.UI.1
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    return {
      agentsList,
      assignableAgents,
      smartAvailabilityActive,
      assistantsList,
      canUseFeature,
      isKaptionFlagEnabled,
    };
  },
  data() {
    return {
      // NOTICE: KAPTION-INTEGRATION — smart-availability.UI.5
      availabilityTimer: null,
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      // NOTICE: KAPTION-INTEGRATION — smart-availability.UI.1
      smartAvailabilityForTeam: 'kaptionSmartAvailability/forTeam',
      smartAvailabilityShowCounts: 'kaptionSmartAvailability/showCounts',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    // NOTICE: KAPTION-INTEGRATION — smart-availability.FLAG.1
    smartAvailabilityFlagEnabled() {
      return this.isKaptionFlagEnabled('KAPTION_SMART_AVAILABILITY');
    },
    // NOTICE: KAPTION-INTEGRATION — smart-availability.PREFERRED.3
    // "Router mode": direct assignment is off for this person but smart assignment is on,
    // so the same pickers stay on screen and a pick asks the resolver instead of assigning.
    routerMode() {
      return (
        this.smartAvailabilityFlagEnabled &&
        this.isKaptionFlagEnabled('KAPTION_SMART_ASSIGNMENT') &&
        !this.canUseFeature('direct_assignment') &&
        this.canUseFeature('smart_assignment')
      );
    },
    pickerAgents() {
      return this.routerMode || !this.canAssignConversations
        ? this.agentsList.filter(agent => agent.id)
        : this.agentsList;
    },
    canAssignConversations() {
      return this.canUseFeature('direct_assignment') || this.routerMode;
    },
    showAssignmentPickers() {
      return this.canAssignConversations || this.smartAvailabilityFlagEnabled;
    },
    teamsList() {
      // NOTICE: KAPTION-INTEGRATION
      // team-availability-guard.UI.2 — the dropdown shows how many members are
      // connected. The suffix is empty when the guard is off, so this is today's
      // list unchanged.
      // smart-availability.UI.1 — with the endpoint's answer the row says who can take
      // work and, when allowed, how much is open; amber when everyone connected is full.
      const inboxId = this.currentChat?.inbox_id;
      const showCounts = !!this.smartAvailabilityShowCounts?.(inboxId);
      const teams = this.teams.map(team => {
        const info = this.smartAvailabilityActive
          ? this.smartAvailabilityForTeam?.(inboxId, team.id)
          : undefined;
        return {
          ...team,
          assignmentLabel: info ? team.name : undefined,
          assignmentDetail: info
            ? teamTitleWithQueue(
                { name: '' },
                info,
                this.$t.bind(this),
                showCounts
              ).replace(/^ · /, '')
            : undefined,
          name: info
            ? teamTitleWithQueue(team, info, this.$t.bind(this), showCounts)
            : teamTitleWithAvailability(team, this.$t.bind(this)),
          // The dropdown renders a green dot for 'online' and nothing otherwise
          // (hide-offline-status), which is the same mark ⌘K draws.
          availability_status: teamStatusFor(
            team,
            info,
            teamAvailabilityStatus(team)
          ),
        };
      });
      if (this.hasAnAssignedTeam && !this.routerMode) {
        return [{ id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') }, ...teams];
      }
      return teams;
    },
    assignedAgent: {
      get() {
        const assignee = this.currentChat.meta.assignee;
        return this.isKaptionAssistant(assignee) ? null : assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    // NOTICE: KAPTION-INTEGRATION
    assignedAssistant: {
      get() {
        const assignee = this.currentChat.meta.assignee;
        return this.isKaptionAssistant(assignee) ? assignee : null;
      },
      set(assistant) {
        const assistantId = assistant ? assistant.id : 0;
        this.$store.dispatch('setCurrentChatAssistant', assistant);
        this.$store
          .dispatch('assignAssistant', {
            conversationId: this.currentChat.id,
            assistantId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_ASSISTANT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.canAssignConversations) return false;
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  beforeUnmount() {
    this.onPickerClose();
  },
  methods: {
    // NOTICE: KAPTION-INTEGRATION — smart-availability.UI.5
    // Ask while a picker is on screen, every fifteen seconds; stop when it closes. A 404
    // (flag off, endpoint not configured) ends as "no data" in the store, which renders as
    // today's list — nothing here can break the picker.
    refreshSmartAvailability() {
      const inboxId = this.currentChat?.inbox_id;
      if (!this.smartAvailabilityFlagEnabled || !inboxId) return;
      this.$store.dispatch('kaptionSmartAvailability/fetch', {
        inboxId,
        conversationId: this.currentChat?.id,
      });
    },
    onPickerOpen() {
      this.refreshSmartAvailability();
      this.onPickerClose();
      if (!this.smartAvailabilityFlagEnabled) return;
      this.availabilityTimer = setInterval(
        () => this.refreshSmartAvailability(),
        15000
      );
    },
    onPickerClose() {
      if (this.availabilityTimer) clearInterval(this.availabilityTimer);
      this.availabilityTimer = null;
    },
    // NOTICE: KAPTION-INTEGRATION — smart-availability.PREFERRED.3
    // The pick goes to the resolver as a hint. Its answer — assigned, or refused with the
    // reason — is what the person sees; nothing is assumed on this side.
    async requestSmartAssignment({ preferredAssigneeId, preferredTeamId }) {
      try {
        const { data } = await KaptionSmartAssignmentAPI.resolve(
          this.currentChat.id,
          { preferredAssigneeId, preferredTeamId }
        );
        useAlert(smartAssignmentAlertMessage(data, this.$t.bind(this)));
        this.refreshSmartAvailability();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.SMART_ASSIGNMENT.ERROR'));
      }
    },
    // NOTICE: KAPTION-INTEGRATION
    isKaptionAssistant(user) {
      if (!user || !user.email) {
        return false;
      }
      return /@assistants\.kaptionai\.com$/i.test(user.email);
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      // smart-availability.PREFERRED.3 — self-assignment in router mode is a pick like any
      // other: the resolver checks the person's own queue.
      if (this.routerMode) {
        this.requestSmartAssignment({ preferredAssigneeId: id });
        return;
      }
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (!this.canAssignConversations) return;
      // NOTICE: KAPTION-INTEGRATION — smart-availability.UI.4 / .PREFERRED.3
      // The dropdown hands back the DECORATED row (name carries the word and the queue);
      // everything downstream must get the real agent, or the header would read
      // "Giuliano Annetti · Al tope · 11 / 5" as the assignee's name.
      const agent =
        this.assignableAgents.find(item => item.id === selectedItem?.id) ||
        selectedItem;
      if (this.routerMode) {
        if (agent?.id) {
          this.requestSmartAssignment({ preferredAssigneeId: agent.id });
        }
        return;
      }
      if (this.assignedAgent && this.assignedAgent.id === agent.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = agent;
      }
    },
    // NOTICE: KAPTION-INTEGRATION
    onClickAssignAssistant(selectedAssistant) {
      if (
        this.assignedAssistant &&
        this.assignedAssistant.id === selectedAssistant.id
      ) {
        this.assignedAssistant = null;
      } else {
        this.assignedAssistant = selectedAssistant;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (!this.canAssignConversations) return;
      // NOTICE: KAPTION-INTEGRATION
      // team-availability-guard.UI.1 — refuse a team nobody is connected to, and say
      // why. Unassigning (the "None" row, id 0) is always allowed: taking a team off a
      // conversation cannot strand it.
      // The dropdown hands back the DECORATED option (name carries the availability
      // suffix); everything downstream must get the real team, or the sidebar would
      // show "Ventas Rosario · 3 conectados" as the assigned team's name.
      const team =
        this.teams.find(item => item.id === selectedItemTeam?.id) ||
        selectedItemTeam;

      // smart-availability.PREFERRED.3 — in router mode the pick goes to the resolver, which
      // picks within the team or explains why nobody can take it.
      if (this.routerMode) {
        if (team?.id) this.requestSmartAssignment({ preferredTeamId: team.id });
        return;
      }
      // Silent by design: the row carries no green dot, and clicking it does nothing.
      // An alert would blame the person for choosing what we put in front of them.
      // smart-availability.UI.1 — with the endpoint's answer the guard's own count is
      // superseded: a team it marked available is assignable whatever the guard says.
      const smartInfo = this.smartAvailabilityActive
        ? this.smartAvailabilityForTeam?.(this.currentChat?.inbox_id, team?.id)
        : undefined;
      if (team?.id && !smartInfo && !isTeamAssignable(team)) return;
      if (this.assignedTeam && this.assignedTeam.id === team.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = team;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div class="bg-n-background">
    <!-- NOTICE: KAPTION-INTEGRATION — smart-availability.PREFERRED.3: shown in router mode too -->
    <div v-if="showAssignmentPickers" class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="pickerAgents"
        :read-only="!canAssignConversations"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
        @open="onPickerOpen"
        @close="onPickerClose"
      />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION -->
    <div
      v-if="
        canUseFeature('direct_assignment') &&
        canUseFeature('assign_ai_assistant')
      "
      class="multiselect-wrap--small"
    >
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSISTANT_LABEL')"
      />
      <MultiselectDropdown
        :options="assistantsList"
        :selected-item="assignedAssistant"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.ASSISTANT')"
        :multiselector-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.ASSISTANT_PLACEHOLDER')
        "
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.ASSISTANT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.ASSISTANT')
        "
        @select="onClickAssignAssistant"
      />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION — smart-availability.PREFERRED.3: shown in router mode too -->
    <div v-if="showAssignmentPickers" class="multiselect-wrap--small">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :read-only="!canAssignConversations"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
        @open="onPickerOpen"
        @close="onPickerClose"
      />
    </div>
    <div class="multiselect-wrap--small">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>
